import type { CastRefEventStore } from "applesauce-common/casts/cast";
import type { IEventStore } from "applesauce-core/event-store";
import { mapEventsToStore } from "applesauce-core";
import type { Filter } from "applesauce-core/helpers";
import { makeCacheRequest } from "applesauce-loaders/helpers";
import { onlyEvents } from "applesauce-relay";
import type { RelayGroup } from "applesauce-relay";
import { combineLatest, merge, of, type Observable } from "rxjs";
import {
  catchError,
  endWith,
  filter,
  ignoreElements,
  map,
  share,
  startWith,
} from "rxjs/operators";
import {
  CONTAINER_REPOSITORY_KIND,
  ContainerRepository,
  isValidContainerRepository,
} from "@/casts/ContainerRepository";
import { use$ } from "@/hooks/use$";
import { useEventStore } from "@/hooks/useEventStore";
import { resilientSubscription } from "@/lib/resilientSubscription";
import { normalizeUrl } from "@/lib/url";
import { relayGroupUrls$ } from "@/models/RepositoryRelayGroup";
import { cacheRequest } from "@/services/cache";
import { addressLoader, pool } from "@/services/nostr";

export interface RepoContainerRepositories {
  repositories: ContainerRepository[];
  settled: boolean;
}

export interface AccountContainerRepositories {
  repositories: ContainerRepository[];
  settled: boolean;
}

function isWebSocketUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "wss:" || url.protocol === "ws:";
  } catch {
    return false;
  }
}

function uniqueRelayUrls(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    if (!isWebSocketUrl(value)) continue;
    const normalized = normalizeUrl(value);
    if (seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(normalized);
  }
  return result;
}

function hydrateCacheIntoStore(
  filters: Filter[],
  store: IEventStore,
): Observable<boolean> {
  return makeCacheRequest(cacheRequest, filters).pipe(
    mapEventsToStore(store),
    ignoreElements(),
    endWith(true),
    catchError(() => of(true)),
    startWith(false),
  );
}

function loadRelayIntoStore(
  relay: string,
  filters: Filter[],
  store: IEventStore,
): Observable<boolean> {
  const raw$ = resilientSubscription(pool, [relay], filters).pipe(
    catchError(() => of("EOSE" as const)),
    share(),
  );

  return merge(
    raw$.pipe(onlyEvents(), mapEventsToStore(store), ignoreElements()),
    raw$.pipe(
      filter((message): message is "EOSE" => message === "EOSE"),
      map(() => true),
    ),
  ).pipe(startWith(false));
}

function loadIntoStoreUntilSettled(
  relays: string[],
  filters: Filter[],
  store: IEventStore,
): Observable<boolean> {
  const relaySettled$ =
    relays.length === 0
      ? of(false)
      : combineLatest(
          relays.map((relay) => loadRelayIntoStore(relay, filters, store)),
        ).pipe(map((settled) => settled.every(Boolean)));

  return combineLatest([
    hydrateCacheIntoStore(filters, store),
    relaySettled$,
  ]).pipe(
    map(([cacheSettled, relaysSettled]) => cacheSettled && relaysSettled),
  );
}

function castRepositories(
  events: Parameters<typeof isValidContainerRepository>[0][],
  store: CastRefEventStore,
): ContainerRepository[] {
  return events
    .flatMap((event) => {
      if (!isValidContainerRepository(event)) return [];
      try {
        return [new ContainerRepository(event, store)];
      } catch {
        return [];
      }
    })
    .sort((left, right) => left.title.localeCompare(right.title));
}

/** Discover trusted ncontainer repositories linked to this NIP-34 repository. */
export function useContainerRepositories(
  repoCoords: string[] | undefined,
  maintainerPubkeys: string[] | undefined,
  repoRelayGroup: RelayGroup | undefined,
): RepoContainerRepositories {
  const store = useEventStore();
  const castStore = store as unknown as CastRefEventStore;
  const coordsKey = [...(repoCoords ?? [])].sort().join(",");
  const maintainerKey = [...(maintainerPubkeys ?? [])].sort().join(",");
  const repoRelays =
    use$(() => relayGroupUrls$(repoRelayGroup), [repoRelayGroup]) ?? [];
  const repoRelayKey = repoRelays.join(",");
  const repositoryFilter: Filter = {
    kinds: [CONTAINER_REPOSITORY_KIND],
    authors: maintainerPubkeys ?? [],
    "#a": repoCoords ?? [],
  } as Filter;

  const settled =
    use$(() => {
      if (!repoCoords?.length || !maintainerPubkeys?.length) return of(true);
      return loadIntoStoreUntilSettled(repoRelays, [repositoryFilter], store);
    }, [coordsKey, maintainerKey, repoRelayKey, store]) ?? false;

  const repositories =
    use$(() => {
      if (!repoCoords?.length || !maintainerPubkeys?.length) return of([]);
      return store
        .timeline([repositoryFilter])
        .pipe(map((events) => castRepositories(events, castStore)));
    }, [coordsKey, maintainerKey, store]) ?? [];

  return { repositories, settled };
}

/**
 * Discover ncontainer repositories controlled by one account so a maintainer
 * can republish one with this repository's `a` coordinates.
 */
export function useAccountContainerRepositories(
  pubkey: string | undefined,
  repoRelayGroup: RelayGroup | undefined,
): AccountContainerRepositories {
  const store = useEventStore();
  const castStore = store as unknown as CastRefEventStore;
  const repoRelays =
    use$(() => relayGroupUrls$(repoRelayGroup), [repoRelayGroup]) ?? [];
  const repoRelayKey = repoRelays.join(",");

  const mailboxSettled =
    use$(() => {
      if (!pubkey) return of(true);
      return addressLoader({ kind: 10002, pubkey }).pipe(
        ignoreElements(),
        endWith(true),
        catchError(() => of(true)),
        startWith(false),
      );
    }, [pubkey]) ?? false;

  const outboxRelays =
    use$(() => {
      if (!pubkey) return of([]);
      return store
        .mailboxes(pubkey)
        .pipe(map((mailboxes) => uniqueRelayUrls(mailboxes?.outboxes ?? [])));
    }, [pubkey, store]) ?? [];
  const relays = uniqueRelayUrls([...repoRelays, ...outboxRelays]);
  const relayKey = relays.join(",");
  const repositoryFilter: Filter = {
    kinds: [CONTAINER_REPOSITORY_KIND],
    authors: pubkey ? [pubkey] : [],
    limit: 200,
  } as Filter;

  const settled =
    use$(() => {
      if (!pubkey) return of(true);
      if (!mailboxSettled) return of(false);
      return loadIntoStoreUntilSettled(relays, [repositoryFilter], store);
    }, [pubkey, mailboxSettled, repoRelayKey, relayKey, store]) ?? false;

  const repositories =
    use$(() => {
      if (!pubkey) return of([]);
      return store
        .timeline([repositoryFilter])
        .pipe(map((events) => castRepositories(events, castStore)));
    }, [pubkey, store]) ?? [];

  return { repositories, settled };
}
